/**
 * REST API client for personas + conversations endpoints.
 * Uses the API Gateway base URL with JWT bearer auth.
 *
 * The `request` helper is exported so sibling service files
 * (projects.js, documents.js, usage.js in §7g) can share the same
 * JWT wiring without duplicating the fetch + auth plumbing.
 */

import { getIdToken } from "./auth.js";

const API_BASE = (import.meta.env.VITE_API_ENDPOINT || "").replace(/\/$/, "");

// Phase 35.3: super-admins set X-Tenant-Id via the TenantSwitcher. This
// module-level variable is set by setActiveTenantHeader() from the store.
let _activeTenantId = null;
export function setActiveTenantHeader(tenantId) {
  _activeTenantId = tenantId || null;
}
export function getActiveTenantHeader() {
  return _activeTenantId;
}

export async function request(path, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };
  const token = getIdToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (_activeTenantId) headers["X-Tenant-Id"] = _activeTenantId;

  const response = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`${response.status}: ${text || response.statusText}`);
  }

  if (response.status === 204) return null;
  return response.json();
}

// ─── Personas ─────────────────────────────────────────────────────────────────

export const listPersonas = () => request("/api/personas");
export const getPersona = (id) => request(`/api/personas/${id}`);

// ─── Conversations ────────────────────────────────────────────────────────────

/**
 * List the current user's conversations.
 *
 * @param {string|null} [projectId=null]
 *   - When a string, scopes the list to that project — the backend
 *     filters via `?projectId=...`.
 *   - When `null` (or omitted), the backend returns ALL of the user's
 *     conversations including project-scoped ones. Callers that want
 *     only standalone conversations must filter client-side on the
 *     returned `projectId` field (see Sidebar.jsx for this pattern).
 */
export const listConversations = (projectId = null) => {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
  return request(`/api/conversations${qs}`);
};
export const getConversation = (id) => request(`/api/conversations/${id}`);
export const deleteConversation = (id) =>
  request(`/api/conversations/${id}`, { method: "DELETE" });
